import React, { useState, useEffect, useRef } from 'react';
import './CopyQuestJsonModal.css';

const PREFERRED_QUEST_INFO_FIELD_ORDER = [
  'uuid',
  'quest_name',
  'quest_description',
  'final_message',
];

// Promo copy lives in the dedicated quest_promo table (edited in the "Промо"
// panel), not in quest_info — so it is excluded from the JSON export.
const EXCLUDED_QUEST_INFO_FIELDS = new Set([
  'promo_short_quest_description',
  'promo_long_quest_description',
]);

const PREFERRED_LOCATION_FIELD_ORDER = [
  'id',
  'uuid',
  'name',
  'coordinates',
  'type',
  'branching_message',
  'branches',
  'tags',
  'comment',
  'description',
  'task',
  'answer_variations',
  'hint',
  'after_message',
];

const CopyQuestJsonModal = ({ questInfo, locations, onClose, onCopy, onSaveFile }) => {
  const [questInfoFields, setQuestInfoFields] = useState({});
  const [locationFields, setLocationFields] = useState({});
  const [sortedQuestInfoKeys, setSortedQuestInfoKeys] = useState([]);
  const [sortedLocationKeys, setSortedLocationKeys] = useState([]);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const copyToastTimeoutRef = useRef(null);

  useEffect(() => {
    if (questInfo) {
      const allKeys = Object.keys(questInfo).filter((k) => !EXCLUDED_QUEST_INFO_FIELDS.has(k));
      const sortedKeys = [...allKeys].sort((a, b) => {
        const indexA = PREFERRED_QUEST_INFO_FIELD_ORDER.indexOf(a);
        const indexB = PREFERRED_QUEST_INFO_FIELD_ORDER.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
      setSortedQuestInfoKeys(sortedKeys);

      const initialFields = allKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setQuestInfoFields(initialFields);
    }
    if (locations && locations.length > 0) {
      const allKeys = locations.reduce((keys, loc) => {
        Object.keys(loc).forEach(key => keys.add(key));
        return keys;
      }, new Set());

      const sortedKeys = Array.from(allKeys).sort((a, b) => {
        const indexA = PREFERRED_LOCATION_FIELD_ORDER.indexOf(a);
        const indexB = PREFERRED_LOCATION_FIELD_ORDER.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
      setSortedLocationKeys(sortedKeys);

      const initialFields = {};
      allKeys.forEach(key => {
        initialFields[key] = true;
      });
      setLocationFields(initialFields);
    }
  }, [questInfo, locations]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyToastTimeoutRef.current) {
        clearTimeout(copyToastTimeoutRef.current);
      }
    };
  }, []);

  const handleQuestInfoChange = (field) => {
    setQuestInfoFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleLocationChange = (field) => {
    setLocationFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSelectAll = (type, value) => {
    if (type === 'quest_info') {
      const newFields = Object.keys(questInfoFields).reduce((acc, key) => {
        acc[key] = value;
        return acc;
      }, {});
      setQuestInfoFields(newFields);
    } else if (type === 'locations') {
      const newFields = Object.keys(locationFields).reduce((acc, key) => {
        acc[key] = value;
        return acc;
      }, {});
      setLocationFields(newFields);
    }
  };

  const handleCopy = () => {
    onCopy({
      questInfoFields,
      locationFields,
      questInfoOrder: sortedQuestInfoKeys,
      locationOrder: sortedLocationKeys,
    });
    
    // Show toast
    setShowCopyToast(true);
    if (copyToastTimeoutRef.current) {
      clearTimeout(copyToastTimeoutRef.current);
    }
    copyToastTimeoutRef.current = setTimeout(() => {
      setShowCopyToast(false);
    }, 1000);
  };

  const handleSaveFile = () => {
    if (onSaveFile) {
      onSaveFile({
        questInfoFields,
        locationFields,
        questInfoOrder: sortedQuestInfoKeys,
        locationOrder: sortedLocationKeys,
      });
    }
  };

  const highlightedFields = ['id', 'comment', 'branches', 'branching_message', 'tags', 'type', 'after_message_voiceover', 'description_voiceover'];

  const handleDeselectUnimportantLocations = () => {
    setLocationFields(prev => {
      const next = { ...prev };
      highlightedFields.forEach(f => {
        if (f in next) next[f] = false;
      });
      return next;
    });
  };

  return (
    <div className="copy-modal-overlay">
      <div className="copy-modal-dialog">
        <h2>Select Fields to Copy</h2>
        <div className="copy-modal-content">
          <div className="field-section">
            <h3>Quest Info</h3>
            <div className="select-all-buttons">
              <button onClick={() => handleSelectAll('quest_info', true)}>Select All</button>
              <button onClick={() => handleSelectAll('quest_info', false)}>Deselect All</button>
            </div>
            <div className="checkbox-group">
              {sortedQuestInfoKeys.map(field => (
                <label key={field}>
                  <input
                    type="checkbox"
                    checked={questInfoFields[field]}
                    onChange={() => handleQuestInfoChange(field)}
                  />
                  {field}
                </label>
              ))}
            </div>
          </div>
          <div className="field-section">
            <h3>Locations</h3>
             <div className="select-all-buttons">
              <button onClick={() => handleSelectAll('locations', true)}>Select All</button>
              <button onClick={() => handleSelectAll('locations', false)}>Deselect All</button>
              <button onClick={handleDeselectUnimportantLocations}>Deselect Unimportant</button>
            </div>
            <div className="checkbox-group">
              {sortedLocationKeys.map(field => (
                <label key={field} className={highlightedFields.includes(field) ? 'highlighted-field' : ''}>
                  <input
                    type="checkbox"
                    checked={locationFields[field]}
                    onChange={() => handleLocationChange(field)}
                  />
                  {field}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="copy-modal-actions">
          <button onClick={onClose} className="close-button">Close</button>
          <button onClick={handleSaveFile} className="save-file-button">💾 Save as file</button>
          <div className="copy-button-wrapper">
            <button onClick={handleCopy} className="copy-button">Copy JSON</button>
            {showCopyToast && <span className="copy-toast-inline">Copied!</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyQuestJsonModal;
